import React, { useState } from 'react';
import { Upload, Sparkles, CheckCircle, Truck, ChevronDown, MessageCircle } from 'lucide-react';

const WHATSAPP = '918889616972';

const HOW_IT_WORKS = [
  {
    icon: Upload,
    step: '01',
    title: 'Send Your Photo',
    desc: 'DM us on WhatsApp or Instagram with a clear, well-lit photo. Front-facing shots work best.',
    color: 'bg-[hsl(var(--lavender))]',
    iconColor: 'text-[hsl(var(--lavender-dark))]',
  },
  {
    icon: Sparkles,
    step: '02',
    title: 'AI Creates Artwork',
    desc: 'Within 24 hours, our AI transforms your photo into beautiful anime-style artwork for your review.',
    color: 'bg-[hsl(350,70%,65%)]/10',
    iconColor: 'text-[hsl(350,70%,55%)]',
  },
  {
    icon: CheckCircle,
    step: '03',
    title: 'Approve & We Print',
    desc: 'Review your design. Request up to 2 free revisions, then we print and ship it to you.',
    color: 'bg-green-50',
    iconColor: 'text-green-600',
  },
];

const FAQS = [
  {
    q: 'Can I request changes to my design?',
    a: 'Yes! We share a full preview of your AI-generated artwork before printing. You can request up to 2 free revisions to ensure you\'re completely happy with the result.',
  },
  {
    q: 'What if I don\'t like the final design?',
    a: 'We want you to love your product. If there\'s a quality issue or defect, we\'ll replace it. Since each item is custom-made, we review issues case-by-case — just reach out via WhatsApp.',
  },
  {
    q: 'How long does delivery take?',
    a: 'T-shirts are delivered within 5–7 business days across India. Hoodies take 7–10 business days. Delivery timelines start after artwork approval.',
  },
  {
    q: 'What photo quality do I need to send?',
    a: 'Send a clear, well-lit, front-facing photo. Higher resolution = better artwork. We\'ll let you know if we need a retake before starting.',
  },
  {
    q: 'Do you ship outside India?',
    a: 'Currently we ship pan-India only. International shipping is coming soon — follow us on Instagram for updates!',
  },
];

const FAQItem = ({ faq }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden hover:border-[hsl(350,70%,65%)]/30 transition-colors">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-white hover:bg-[hsl(var(--cream))]/50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-900">{faq.q}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        <div className="px-5 pb-4 bg-white border-t border-gray-50">
          <p className="text-sm text-gray-600 leading-relaxed pt-3">{faq.a}</p>
        </div>
      )}
    </div>
  );
};

/**
 * ProductPageEnhancements
 * Drop this into any product / category detail page
 */
const ProductPageEnhancements = ({ collectionTitle = 'This Collection' }) => {
  const handleWhatsApp = () => {
    if (typeof window !== 'undefined') {
      if (window.gtag) window.gtag('event', 'whatsapp_click', { location: 'product_page' });
      if (window.fbq) window.fbq('trackCustom', 'whatsapp_click', { location: 'product_page' });
    }
    const msg = `Hi AuraPrintiqo! 👋\n\nI want to order from ${collectionTitle}.\n\nCan you help me get started?`;
    window.open(
      `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="space-y-14">
      {/* ── How It Works (3 steps) ── */}
      <section className="py-12 bg-gradient-to-b from-[hsl(var(--cream))]/60 to-white rounded-3xl">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 bg-[hsl(350,70%,65%)]/10 rounded-full text-sm font-medium text-[hsl(350,70%,55%)] mb-3">
              Simple Process
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">How It Works</h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="relative text-center">
                  {i < HOW_IT_WORKS.length - 1 && (
                    <div className="hidden sm:block absolute top-7 left-[60%] right-0 h-0.5 bg-gradient-to-r from-gray-200 to-transparent" />
                  )}
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${item.color} mb-4 shadow-soft relative`}>
                    <Icon className={`w-6 h-6 ${item.iconColor}`} />
                    <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-gray-800 text-white text-[10px] font-bold flex items-center justify-center">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Delivery Info ── */}
      <section className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-soft">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
            <Truck className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delivery Info</h3>
            <ul className="space-y-1.5 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                T-shirts: delivered in <strong className="text-gray-800">5–7 business days</strong> across India
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                Hoodies: delivered in <strong className="text-gray-800">7–10 business days</strong> across India
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                Timeline starts <strong className="text-gray-800">after your artwork approval</strong>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                Tracking link shared via WhatsApp once dispatched
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section>
        <div className="text-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Frequently Asked Questions</h2>
          <p className="text-gray-600 text-sm">Still have questions? Chat with us on WhatsApp.</p>
        </div>

        <div className="space-y-2 max-w-2xl mx-auto">
          {FAQS.map((faq, i) => (
            <FAQItem key={i} faq={faq} />
          ))}
        </div>

        <div className="text-center mt-8">
          <button
            onClick={handleWhatsApp}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold shadow hover:shadow-lg transition-all hover:scale-105 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
          >
            <MessageCircle className="w-4 h-4" />
            Have more questions? Chat with us
          </button>
        </div>
      </section>
    </div>
  );
};

export default ProductPageEnhancements;
